import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  MenuItem,
  InputAdornment,
  Stack,
  Divider,
  Skeleton,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useAuthContext } from "../context/AuthContext";
import { fetchData } from "../api/client";
import { useCompanies } from "../hooks/useCompanies";

const TIPOS = [
  { value: 0, label: "Default" },
  { value: 1, label: "Correios" },
  { value: 2, label: "Vuxx" },
  { value: 3, label: "Retirada" },
  { value: 4, label: "Gestão de Envios" },
];

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

function buildDaysLabel(minDays, maxDays, tipo) {
  if (Number(tipo) === 3) {
    const pronta = Number(maxDays);
    const encomenda = Number(minDays);

    if (pronta || encomenda) {
      const p = pronta ? `${pronta}d pronta` : "";
      const e = encomenda ? `${encomenda}d encomenda` : "";
      return [p, e].filter(Boolean).join(" / ");
    }
    return "Retirada";
  }

  const minOk = Number(minDays) > 0;
  const maxOk = Number(maxDays) > 0;
  if (minOk && maxOk) return `${Number(minDays)}-${Number(maxDays)}d`;
  if (minOk) return `${Number(minDays)}d`;
  if (maxOk) return `${Number(maxDays)}d`;
  return "3-7d";
}

function getSelectedCompanyIds() {
  try {
    const raw = sessionStorage.getItem("selectedCompanies");
    const arr = JSON.parse(raw || "[]");
    return Array.isArray(arr) ? arr.filter((n) => Number.isFinite(Number(n))).map(Number) : [];
  } catch {
    return [];
  }
}

const AddDeliveryMethodForm = ({ deliveryMethod, onClose, onSubmit, isFetching }) => {
  const { userData } = useAuthContext();

  const { data: compData, isLoading: companiesLoading } = useCompanies(1, 500, "");
  const companies = compData?.companies || [];

  const selectedCompanyIds = useMemo(() => getSelectedCompanyIds(), []);
  const defaultProjectId =
    selectedCompanyIds.length ? selectedCompanyIds[0] : companies[0]?.id ?? null;

  const [formData, setFormData] = useState(() => ({
    id: deliveryMethod?.id || undefined,
    id_projeto: deliveryMethod?.id_projeto ?? defaultProjectId ?? null,
    id_usuario: userData?.id || null,
    id_usuario_ultima_alteracao: userData?.id || null,
    nome: "",
    descricao: "",
    status: 1,
    tipo: 1,
    valor_minimo: "",
    valor_maximo: "",
    acrescimo: "",
    desconto: "",
    tempo_entrega_minimo: "",
    tempo_entrega_maximo: "",
    ceps_permitidos: "",
    cep_origem: "",
    chave: "",
    codigoservico: "",
    json: "",
  }));
  const [loadingById, setLoadingById] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [vuxxConfig, setVuxxConfig] = useState({
    environment: "production",
    originZip: "",
    storeId: "",
    customUrl: "",
  });
  const [showVuxxAdvanced, setShowVuxxAdvanced] = useState(false);

  const projectOptions = useMemo(() => {
    const map = new Map(companies.map((c) => [Number(c.id), c]));
    const ids =
      selectedCompanyIds.length
        ? selectedCompanyIds
        : companies.length
        ? companies.map((c) => Number(c.id))
        : [];

    const base = ids.map((pid) => {
      const c = map.get(Number(pid));
      const labelBase = c?.nome || c?.fantasia || c?.razao_social || `Empresa #${pid}`;
      return { id: Number(pid), label: `${labelBase} (ID ${pid})` };
    });

    if (formData.id_projeto != null && !base.some((o) => o.id === Number(formData.id_projeto))) {
      base.unshift({
        id: Number(formData.id_projeto),
        label: `Empresa #${formData.id_projeto} (ID ${formData.id_projeto})`,
      });
    }

    return base;
  }, [companies, selectedCompanyIds, formData.id_projeto]);

  useEffect(() => {
    const id = deliveryMethod?.id;
    if (!id) return;
    setLoadingById(true);
    setLoadError(null);
    fetchData(`/delivery-methods/${id}`, "GET")
      .then((resp) => {
        const d = resp?.data || {};
        setFormData((prev) => ({
          ...prev,
          ...d,
          id,
          id_projeto: d.id_projeto ?? prev.id_projeto ?? defaultProjectId ?? null,
          id_usuario: d.id_usuario ?? prev.id_usuario ?? userData?.id ?? null,
          id_usuario_ultima_alteracao: userData?.id ?? prev.id_usuario_ultima_alteracao ?? null,
          nome: d.nome ?? "",
          descricao: d.descricao ?? "",
          status: d.status ?? 1,
          tipo: d.tipo ?? 1,
          valor_minimo: d.valor_minimo ?? "",
          valor_maximo: d.valor_maximo ?? "",
          acrescimo: d.acrescimo ?? "",
          desconto: d.desconto ?? "",
          tempo_entrega_minimo: d.tempo_entrega_minimo ?? "",
          tempo_entrega_maximo: d.tempo_entrega_maximo ?? "",
          ceps_permitidos: d.ceps_permitidos ?? "",
          cep_origem: d.cep_origem ?? "",
          chave: d.chave ?? "",
          codigoservico: d.codigoservico ?? "",
          json: d.json ?? "",
        }));

        if (Number(d.tipo) === 2 && d.json) {
          try {
            const parsed = JSON.parse(d.json);
            setVuxxConfig((prevCfg) => ({
              ...prevCfg,
              customUrl:
                typeof parsed?.url === "string" ? parsed.url : prevCfg.customUrl,
              originZip:
                typeof parsed?.json?.origin?.zipCode === "string"
                  ? parsed.json.origin.zipCode
                  : prevCfg.originZip,
              storeId:
                typeof parsed?.json?.storeId === "string"
                  ? parsed.json.storeId
                  : prevCfg.storeId,
              environment:
                typeof parsed?.url === "string" && parsed.url.toLowerCase().includes("sandbox")
                  ? "sandbox"
                  : prevCfg.environment,
            }));
          } catch {
            // ignore parse errors; user pode usar modo avançado
          }
        }
      })
      .catch((e) => setLoadError(e?.response?.data?.message || "Falha ao carregar método."))
      .finally(() => setLoadingById(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMethod?.id]);

  const isTipo = (t) => Number(formData.tipo) === Number(t);

  const daysPreview = useMemo(
    () => buildDaysLabel(formData.tempo_entrega_minimo, formData.tempo_entrega_maximo, formData.tipo),
    [formData.tempo_entrega_minimo, formData.tempo_entrega_maximo, formData.tipo]
  );

  const buildVuxxJsonFromConfig = () => {
    const env = vuxxConfig.environment === "sandbox" ? "sandbox" : "production";
    const baseUrl =
      (vuxxConfig.customUrl || "").trim() ||
      (env === "sandbox"
        ? "https://sandbox-api.boxdelivery.com.br/quotes"
        : "https://api.boxdelivery.com.br/quotes");

    const originZip = onlyDigits(vuxxConfig.originZip).slice(0, 8);

    const payload = {
      url: baseUrl,
      method: "POST",
      header: {
        Authorization: "Bearer #TOKEN#",
        "Content-Type": "application/json",
      },
      json: {
        origin: {
          zipCode: originZip || undefined,
        },
        destination: {
          zipCode: "#PEOPLELOCATIONZIP#",
        },
        package: {
          totalWeightGrams: "#TOTALWEIGHT#",
        },
        schedule: {
          pickupDateTime: "#TOMORROW#",
        },
        ...(vuxxConfig.storeId
          ? {
              storeId: vuxxConfig.storeId,
            }
          : {}),
      },
    };

    return JSON.stringify(payload);
  };

  const hasStoredSecret = (field) => !!formData[`has_${field}`];
  const maskSecretField = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: "",
      [`has_${field}`]: false,
    }));
  };

  const validate = () => {
    if (!String(formData.nome).trim()) return "Informe o nome do método.";
    if (formData.id_projeto == null) return "Empresa obrigatória.";
    if (![0, 1].includes(Number(formData.status))) return "Status inválido.";
    if (isTipo(1)) {
      if (onlyDigits(formData.cep_origem).length !== 8) return "CEP de origem (Correios) inválido.";
      if (!String(formData.chave).trim() && !hasStoredSecret("chave")) return "Token (Correios) obrigatório.";
      if (!String(formData.codigoservico).trim()) return "Código de serviço (Correios) obrigatório.";
    }
    if (isTipo(2)) {
      if (!String(formData.chave).trim() && !hasStoredSecret("chave")) return "Token (Vuxx) obrigatório.";
      // JSON deixa de ser obrigatório: será gerado automaticamente a partir da configuração básica
    }
    if (isTipo(4)) {
      if (!String(formData.chave).trim() && !hasStoredSecret("chave")) return "Chave de integração (GE) obrigatória.";
    }
    return null;
  };

  const normalizePayload = () => {
    const p = { ...formData };
    p.status = Number(p.status) === 1 ? 1 : 0;
    p.tipo = Number(p.tipo) || 0;

    const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const intOrNull = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? Math.trunc(n) : null;
    };

    p.valor_minimo = numOrNull(p.valor_minimo);
    p.valor_maximo = numOrNull(p.valor_maximo);
    p.acrescimo = numOrNull(p.acrescimo);
    p.desconto = numOrNull(p.desconto);

    p.tempo_entrega_minimo = intOrNull(p.tempo_entrega_minimo);
    p.tempo_entrega_maximo = intOrNull(p.tempo_entrega_maximo);

    p.cep_origem = onlyDigits(p.cep_origem);
    if (!p.codigoservico && Number(p.tipo) === 4) p.codigoservico = "4162";
    if (p.tipo === 2) {
      if (!p.json || !String(p.json).trim()) {
        p.json = buildVuxxJsonFromConfig();
      }
    } else if (!p.json) {
      p.json = null;
    }
    if (!p.ceps_permitidos) p.ceps_permitidos = null;

    if (p.id_projeto == null) p.id_projeto = defaultProjectId ?? null;

    return p;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cep_origem") {
      setFormData((prev) => ({ ...prev, [name]: onlyDigits(value).slice(0, 8) }));
      return;
    }
    if (name === "tempo_entrega_minimo" || name === "tempo_entrega_maximo") {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, "").slice(0, 3) }));
      return;
    }
    if (name === "id_projeto") {
      const v = Number(value);
      setFormData((prev) => ({ ...prev, id_projeto: Number.isFinite(v) ? v : null }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    const err = validate();
    if (err) {
      setErrorMessage(err);
      setIsSubmitting(false);
      return;
    }

    try {
      await onSubmit(normalizePayload());
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Erro ao salvar o método de entrega.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contentLoading = loadingById && formData?.id;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        width: "95%",
        mx: "auto",
        overflowX: "hidden",
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="h6" gutterBottom>
          {formData?.id ? "Editar Método de Entrega" : "Adicionar Método de Entrega"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preview prazo: <b>{daysPreview}</b>
        </Typography>
      </Stack>

      {loadError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {contentLoading ? (
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={120} />
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ overflowX: "hidden" }}>
            {/* Nome */}
            <Grid item xs={12}>
              <TextField
                label="Nome"
                name="nome"
                value={formData.nome || ""}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ maxLength: 120 }}
              />
            </Grid>

            {/* Status | Tipo | Empresa */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                fullWidth
                select
                required
              >
                <MenuItem value={1}>Ativo</MenuItem>
                <MenuItem value={0}>Inativo</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Tipo"
                name="tipo"
                value={formData.tipo}
                onChange={handleChange}
                fullWidth
                select
                required
              >
                {TIPOS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Empresa"
                name="id_projeto"
                value={formData.id_projeto ?? ""}
                onChange={handleChange}
                fullWidth
                select
                required
                disabled={companiesLoading}
                helperText={
                  companiesLoading
                    ? "Carregando empresas..."
                    : "Selecione a empresa ao qual este método pertencerá"
                }
              >
                {projectOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Descrição */}
            {/* <Grid item xs={12}>
              <TextField
                label="Descrição (opcional)"
                name="descricao"
                value={formData.descricao || ""}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid> */}

            {/* Regras financeiras */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Pedido Mínimo (R$)"
                name="valor_minimo"
                value={formData.valor_minimo ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Pedido Máximo (R$)"
                name="valor_maximo"
                value={formData.valor_maximo ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Acréscimo (R$)"
                name="acrescimo"
                value={formData.acrescimo ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                helperText="Somado ao valor final do frete"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Desconto (R$)"
                name="desconto"
                value={formData.desconto ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                helperText="Subtraído do valor final do frete"
              />
            </Grid>

            {/* Prazo */}
            <Grid item xs={12} md={3}>
              <TextField
                label={
                  Number(formData.tipo) === 3
                    ? "Prazo de entrega sob encomenda (dias)"
                    : "Prazo Mínimo (dias)"
                }
                name="tempo_entrega_minimo"
                value={formData.tempo_entrega_minimo ?? ""}
                onChange={handleChange}
                fullWidth
                inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
                helperText={
                  Number(formData.tipo) === 3
                    ? "Sob encomenda"
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={
                  Number(formData.tipo) === 3
                    ? "Prazo de entrega pronta entrega (dias)"
                    : "Prazo Máximo (dias)"
                }
                name="tempo_entrega_maximo"
                value={formData.tempo_entrega_maximo ?? ""}
                onChange={handleChange}
                fullWidth
                inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
                helperText={
                  Number(formData.tipo) === 3
                    ? "Pronta entrega"
                    : ""
                }
              />
            </Grid>

            {/* Cobertura por CEP */}
            <Grid item xs={12} md={6}>
              <TextField
                label="CEPs Permitidos (prefixos ou faixas)"
                name="ceps_permitidos"
                value={formData.ceps_permitidos || ""}
                onChange={handleChange}
                fullWidth
                placeholder="Ex.: 010-199, 20000-28999, 301, 40000000-41999999"
                helperText={
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ wordBreak: "break-word" }}>
                    <InfoOutlined fontSize="small" />
                    <span>
                      Prefixos (ex.: 301), CEPs exatos (8 dígitos) ou faixas (A-B). Separe por vírgula, espaço ou ponto e vírgula.
                    </span>
                  </Stack>
                }
              />
            </Grid>

            {/* Campos por tipo */}
            {Number(formData.tipo) === 1 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Correios (oficial)</Divider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="CEP de Origem"
                    name="cep_origem"
                    value={formData.cep_origem || ""}
                    onChange={handleChange}
                    fullWidth
                    inputProps={{ maxLength: 8, inputMode: "numeric", pattern: "\\d*" }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  {(() => {
                    const hasToken = hasStoredSecret("chave");
                    const isMasked = hasToken && !formData.chave;
                    return (
                      <TextField
                        label="Token (Bearer)"
                        name="chave"
                        value={isMasked ? "••••••••" : formData.chave || ""}
                        onChange={isMasked ? undefined : handleChange}
                        fullWidth
                        helperText={
                          isMasked
                            ? "Valor já configurado. Clique para alterar."
                            : !formData.chave && hasToken
                            ? "Já existe um Token configurado. Preencha apenas se quiser trocar."
                            : undefined
                        }
                        InputProps={isMasked ? { readOnly: true } : undefined}
                        onClick={isMasked ? () => maskSecretField("chave") : undefined}
                        onFocus={isMasked ? () => maskSecretField("chave") : undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Código de Serviço"
                    name="codigoservico"
                    value={formData.codigoservico || ""}
                    onChange={handleChange}
                    fullWidth
                    placeholder="Ex.: 04014"
                  />
                </Grid>
              </>
            )}

            {Number(formData.tipo) === 2 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Vuxx</Divider>
                </Grid>
                <Grid item xs={12} md={4}>
                  {(() => {
                    const hasToken = hasStoredSecret("chave");
                    const isMasked = hasToken && !formData.chave;
                    return (
                      <TextField
                        label="Token"
                        name="chave"
                        value={isMasked ? "••••••••" : formData.chave || ""}
                        onChange={isMasked ? undefined : handleChange}
                        fullWidth
                        helperText={
                          isMasked
                            ? "Valor já configurado. Clique para alterar."
                            : !formData.chave && hasToken
                            ? "Já existe um Token configurado. Preencha apenas se quiser trocar."
                            : undefined
                        }
                        InputProps={isMasked ? { readOnly: true } : undefined}
                        onClick={isMasked ? () => maskSecretField("chave") : undefined}
                        onFocus={isMasked ? () => maskSecretField("chave") : undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Ambiente"
                    name="vuxx_environment"
                    value={vuxxConfig.environment}
                    onChange={(e) =>
                      setVuxxConfig((prev) => ({
                        ...prev,
                        environment: e.target.value === "sandbox" ? "sandbox" : "production",
                      }))
                    }
                    fullWidth
                    select
                  >
                    <MenuItem value="production">Produção</MenuItem>
                    <MenuItem value="sandbox">Sandbox</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="CEP de Origem (Vuxx)"
                    value={vuxxConfig.originZip}
                    onChange={(e) =>
                      setVuxxConfig((prev) => ({
                        ...prev,
                        originZip: onlyDigits(e.target.value).slice(0, 8),
                      }))
                    }
                    fullWidth
                    inputProps={{ maxLength: 8, inputMode: "numeric", pattern: "\\d*" }}
                    helperText="CEP de coleta usado nas cotações da Vuxx"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="ID da Loja / Conta (opcional)"
                    value={vuxxConfig.storeId}
                    onChange={(e) =>
                      setVuxxConfig((prev) => ({
                        ...prev,
                        storeId: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="URL da API (opcional)"
                    value={vuxxConfig.customUrl}
                    onChange={(e) =>
                      setVuxxConfig((prev) => ({
                        ...prev,
                        customUrl: e.target.value,
                      }))
                    }
                    fullWidth
                    placeholder="Deixe em branco para usar a URL padrão da Vuxx/Box"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowVuxxAdvanced((v) => !v)}
                    sx={{ mt: { xs: 1, md: 3 } }}
                  >
                    {showVuxxAdvanced ? "Ocultar modo avançado" : "Editar JSON avançado"}
                  </Button>
                </Grid>
                {showVuxxAdvanced && (
                  <Grid item xs={12}>
                  <TextField
                      label="JSON de requisição (modo avançado)"
                    name="json"
                    value={formData.json || ""}
                    onChange={handleChange}
                    fullWidth
                    multiline
                      minRows={4}
                      helperText="Use este campo apenas se precisar substituir o JSON gerado automaticamente."
                  />
                </Grid>
                )}
              </>
            )}

            {Number(formData.tipo) === 3 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Retirada</Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Instruções ao cliente (opcional)"
                    name="json"
                    value={formData.json || ""}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Ex.: Endereço da loja, horários, documentos necessários…"
                  />
                </Grid>
              </>
            )}

            {Number(formData.tipo) === 4 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Gestão de Envios</Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  {(() => {
                    const hasKey = hasStoredSecret("chave");
                    const isMasked = hasKey && !formData.chave;
                    return (
                      <TextField
                        label="Chave de Integração"
                        name="chave"
                        value={isMasked ? "••••••••" : formData.chave || ""}
                        onChange={isMasked ? undefined : handleChange}
                        fullWidth
                        helperText={
                          isMasked
                            ? "Valor já configurado. Clique para alterar."
                            : !formData.chave && hasKey
                            ? "Já existe uma chave configurada. Preencha apenas se quiser trocar."
                            : undefined
                        }
                        InputProps={isMasked ? { readOnly: true } : undefined}
                        onClick={isMasked ? () => maskSecretField("chave") : undefined}
                        onFocus={isMasked ? () => maskSecretField("chave") : undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="CEP de Origem (opcional)"
                    name="cep_origem"
                    value={formData.cep_origem || ""}
                    onChange={handleChange}
                    fullWidth
                    inputProps={{ maxLength: 8, inputMode: "numeric", pattern: "\\d*" }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Código de Serviço"
                    name="codigoservico"
                    value={formData.codigoservico || "4162"}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={onClose} disabled={isSubmitting || isFetching}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSubmitting || isFetching}>
              {isFetching || isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
            </Button>
          </Box>
        </form>
      )}
    </Paper>
  );
};

export default AddDeliveryMethodForm;
